/**
 * Observed price history as a step chart.
 *
 * Three rules make this chart honest. Prices hold until they are observed to
 * change, so the line is stepped rather than interpolated. Each source is its
 * own series, because two sources disagreeing is information. And an interval
 * with no known rate is a gap in the line, never a segment drawn between two
 * points that were never connected.
 *
 * A table of the same intervals sits beside it, so the chart is not the only
 * way to read the data.
 */

import type { PriceWindow, TokenBucket } from "@pricing/contracts";

import { formatInstant } from "../lib/format.ts";

const WIDTH = 720;
const HEIGHT = 260;
const PADDING = { top: 16, right: 16, bottom: 32, left: 64 };

/** Series colours come from the token palette, and each also gets a dash. */
const SERIES_STYLES = [
  { stroke: "var(--accent)", dash: "" },
  { stroke: "var(--accent-2)", dash: "6 3" },
  { stroke: "var(--warn)", dash: "2 3" },
  { stroke: "var(--success)", dash: "8 2 2 2" },
] as const;

export interface HistoryChartProps {
  readonly windows: readonly PriceWindow[];
  readonly bucket: TokenBucket;
  readonly fromUtc: string;
  readonly toUtc: string;
}

/**
 * An axis label. Ticks are divisions of the plot, not published rates, so
 * rounding them is fine; the precision still grows until a nonzero tick is
 * visibly nonzero.
 */
function formatTick(value: number): string {
  if (value === 0) return "$0";
  let places = 2;
  while (places < 10 && Number(value.toFixed(places)) === 0) places += 1;
  return `$${value.toFixed(places)}`;
}

interface Segment {
  readonly start: number;
  readonly end: number;
  readonly value: number;
}

function segmentsFor(windows: readonly PriceWindow[], bucket: TokenBucket, to: number): Segment[] {
  return windows
    .map((window) => {
      const rate = window.rate_set[bucket];
      // Anything but a number is a gap: an unknown rate is not zero, and an
      // included rate has no line of its own to draw.
      if (rate.state !== "priced") return null;
      const value = Number(rate.amount);
      if (!Number.isFinite(value)) return null;
      return {
        start: Date.parse(window.start_utc),
        end: window.end_utc === null ? to : Date.parse(window.end_utc),
        value,
      };
    })
    .filter((segment): segment is Segment => segment !== null)
    .sort((a, b) => a.start - b.start);
}

export function HistoryChart(props: HistoryChartProps): React.JSX.Element {
  const from = Date.parse(props.fromUtc);
  const to = Date.parse(props.toUtc);
  const sources = [...new Set(props.windows.map((window) => window.source_id))].sort();
  const series = sources.map((sourceId, index) => ({
    sourceId,
    style: SERIES_STYLES[index % SERIES_STYLES.length],
    segments: segmentsFor(
      props.windows.filter((window) => window.source_id === sourceId),
      props.bucket,
      to,
    ),
  }));

  const values = series.flatMap((entry) => entry.segments.map((segment) => segment.value));
  if (values.length === 0) {
    return (
      <p className="notice notice--empty">
        No numeric {props.bucket.replace(/_/g, " ")} rate is known in this period. Nothing is drawn
        rather than a line through an unknown value.
      </p>
    );
  }

  const maxValue = Math.max(...values) * 1.15;
  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const x = (time: number): number =>
    PADDING.left + (plotWidth * (Math.min(Math.max(time, from), to) - from)) / Math.max(to - from, 1);
  const y = (value: number): number => PADDING.top + plotHeight * (1 - value / Math.max(maxValue, 1e-12));

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ratio * maxValue);

  return (
    <figure style={{ margin: 0 }}>
      <svg
        className="chart"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`Observed ${props.bucket.replace(/_/g, " ")} rate per million tokens, ${formatInstant(props.fromUtc)} to ${formatInstant(props.toUtc)}, one line per source, with gaps where no rate is known.`}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={y(tick)}
              y2={y(tick)}
              stroke="var(--border)"
              strokeWidth={1}
            />
            <text
              x={PADDING.left - 8}
              y={y(tick) + 4}
              textAnchor="end"
              fontSize="11"
              fill="var(--muted)"
            >
              {formatTick(tick)}
            </text>
          </g>
        ))}
        <text x={PADDING.left} y={HEIGHT - 8} fontSize="11" fill="var(--muted)">
          {formatInstant(props.fromUtc)}
        </text>
        <text x={WIDTH - PADDING.right} y={HEIGHT - 8} textAnchor="end" fontSize="11" fill="var(--muted)">
          {formatInstant(props.toUtc)}
        </text>

        {series.map((entry) =>
          entry.segments.map((segment, index) => {
            const previous = entry.segments[index - 1];
            // A riser is drawn only when one interval abuts the next. Where a
            // gap separates them, no line is drawn across it.
            const contiguous = previous !== undefined && previous.end === segment.start;
            return (
              <g key={`${entry.sourceId}-${segment.start}`}>
                {contiguous ? (
                  <line
                    x1={x(segment.start)}
                    x2={x(segment.start)}
                    y1={y(previous.value)}
                    y2={y(segment.value)}
                    stroke={entry.style.stroke}
                    strokeWidth={2}
                    strokeDasharray={entry.style.dash || undefined}
                  />
                ) : null}
                <line
                  x1={x(segment.start)}
                  x2={x(segment.end)}
                  y1={y(segment.value)}
                  y2={y(segment.value)}
                  stroke={entry.style.stroke}
                  strokeWidth={2}
                  strokeDasharray={entry.style.dash || undefined}
                />
              </g>
            );
          }),
        )}
      </svg>

      <figcaption className="chart__legend">
        {series.map((entry) => (
          <span key={entry.sourceId}>
            <span
              className="chart__swatch"
              style={{
                background: entry.style.stroke,
                backgroundImage: entry.style.dash
                  ? `repeating-linear-gradient(90deg, ${entry.style.stroke} 0 6px, transparent 6px 9px)`
                  : undefined,
              }}
              aria-hidden="true"
            />
            {entry.sourceId}
          </span>
        ))}
        <span>Gaps mean no rate is known, not a zero rate.</span>
      </figcaption>
    </figure>
  );
}
